import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from "recharts";
import { TrendingUp, AlertTriangle, Brain, Clock, Target } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = {
  primary: "#00D2FF",
  secondary: "#00BFA5",
  purple: "#A855F7",
  orange: "#F97316",
  red: "#EF4444",
  green: "#10B981",
  blue: "#3B82F6"
};

const RoboMeshInsights = () => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      const res = await fetch(`${API}/robomesh/insights`, {
        credentials: "include"
      });
      const data = await res.json();
      setInsights(data);
    } catch (e) {
      console.error("Insights fetch error:", e);
      // Demo data
      setInsights({
        total_runs: 24,
        domains: { home: 12, factory: 7, logistics: 5 },
        complexity_trend: Array.from({ length: 10 }, (_, i) => ({
          date: new Date(Date.now() - (9 - i) * 86400000).toLocaleDateString(),
          complexity: Math.random() * 8 + 2
        })),
        safety_distribution: { low: 15, medium: 7, high: 2, critical: 0 },
        automation_ready: 62.5,
        models_performance: {
          "GPT-5 Mini": 18,
          "Claude Haiku": 12,
          "Gemini Flash": 15
        }
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading || !insights) {
    return <div className="text-gray-400">Loading insights...</div>;
  }

  const domainData = Object.entries(insights.domains).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const safetyData = [
    { name: "Low Risk", value: insights.safety_distribution.low, fill: COLORS.green },
    { name: "Medium Risk", value: insights.safety_distribution.medium, fill: COLORS.orange },
    { name: "High Risk", value: insights.safety_distribution.high, fill: COLORS.red },
    { name: "Critical", value: insights.safety_distribution.critical, fill: "#DC2626" }
  ];

  const modelData = Object.entries(insights.models_performance).map(([name, value]) => ({
    name,
    uses: value
  }));

  return (
    <div className="space-y-6" data-testid="robomesh-insights">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">RoboMesh Insights</h1>
          <p className="text-gray-400">Analytics for your physical-world intelligence</p>
        </div>
        <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
          {insights.total_runs} Total Runs
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Total Tasks</p>
                <p className="text-3xl font-bold text-white mt-1">{insights.total_runs}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-cyan-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Automation Ready</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{insights.automation_ready.toFixed(0)}%</p>
              </div>
              <Target className="w-8 h-8 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Avg Complexity</p>
                <p className="text-3xl font-bold text-purple-400 mt-1">
                  {(insights.complexity_trend.reduce((sum, d) => sum + d.complexity, 0) / insights.complexity_trend.length).toFixed(1)}
                </p>
              </div>
              <Brain className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">High Risk Tasks</p>
                <p className="text-3xl font-bold text-red-400 mt-1">{insights.safety_distribution.high + insights.safety_distribution.critical}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Complexity Trend */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Complexity Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={insights.complexity_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="date" stroke="#888" tick={{ fill: '#888', fontSize: 12 }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} domain={[0, 10]} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                  labelStyle={{ color: '#fff' }}
                />
                <Line type="monotone" dataKey="complexity" stroke={COLORS.primary} strokeWidth={2} dot={{ fill: COLORS.primary, r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Safety Distribution */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Safety Risk Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={safetyData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {safetyData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Domain Breakdown */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Tasks by Domain</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={domainData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="name" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis stroke="#888" tick={{ fill: '#888' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                />
                <Bar dataKey="value" fill={COLORS.purple} radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Model Performance */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader>
            <CardTitle className="text-white">AI Model Usage</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={modelData} layout="horizontal">
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis type="number" stroke="#888" tick={{ fill: '#888' }} />
                <YAxis dataKey="name" type="category" stroke="#888" tick={{ fill: '#888', fontSize: 12 }} width={100} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                />
                <Bar dataKey="uses" fill={COLORS.secondary} radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default RoboMeshInsights;
